import { t } from "@lingui/core/macro";
import React, { useEffect, useRef, useState } from "react";
import { Pressable, StyleSheet, Animated, Platform } from "react-native";

import { AnimationDurations } from "../../../theme/animations";
import { whooshPrimitives } from "../../../theme/colors/primitives";
import { Box, HStack } from "../../primitive";
import { ToggleSwitchLabel } from "./components/ToggleSwitchLabel";
import { getToggleSwitchTrackColor } from "./utils/getToggleSwitchTrackColor";

export type ToggleSwitchLabelPosition = "left" | "right";

export type ToggleSwitchProps = {
  isChecked: boolean;
  onToggle?: (isChecked: boolean) => void;
  isControlled?: boolean;
  isDisabled?: boolean;
  name?: string;
  accessibilityLabel?: string;
  testID?: string;
  showToggleLabel?: boolean;
  label?: string;
  labelPosition?: ToggleSwitchLabelPosition;
};

export const ToggleSwitch = ({
  isChecked = false,
  onToggle,
  isControlled = false,
  isDisabled,
  accessibilityLabel,
  testID,
  showToggleLabel,
  label,
  labelPosition = "right",
  name,
}: ToggleSwitchProps) => {
  const [internalCheckedState, setInternalCheckedState] = useState(isChecked);
  const checkedState = isControlled ? isChecked : internalCheckedState;
  const toggleAnimation = useRef(new Animated.Value(isChecked ? 1 : 0)).current;
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const isSwitchFocused = !isDisabled && isFocused;
  const switchLabel = showToggleLabel ? (checkedState ? t`Yes` : t`No`) : label;

  useEffect(() => {
    Animated.timing(toggleAnimation, {
      toValue: checkedState ? 1 : 0,
      duration: AnimationDurations.moderate01,
      useNativeDriver: false,
    }).start();
  }, [checkedState, toggleAnimation]);

  useEffect(() => {
    setInternalCheckedState(isChecked);
  }, [isChecked]);

  const toggleSwitch = () => {
    if (!isDisabled) {
      const nextCheckedState = !checkedState;

      if (!isControlled) {
        setInternalCheckedState(nextCheckedState);
      }

      onToggle?.(nextCheckedState);
    }
  };

  const thumbLeft = toggleAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: [3, 15],
  });

  const onHover = () => {
    if (!isDisabled) {
      setIsHovered(true);
    }
  };
  const onHoverOut = () => {
    setIsHovered(false);
  };

  const onFocus = () => {
    if (!isDisabled) {
      setIsFocused(true);
    }
  };

  const onBlur = () => {
    setIsFocused(false);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === " " || event.key === "Spacebar") {
      event.preventDefault();
      toggleSwitch();
    }
  };

  const switchTrackColor = getToggleSwitchTrackColor({
    isChecked: checkedState,
    isDisabled,
    isHovered,
  });

  const switchThumbColor = isDisabled
    ? whooshPrimitives.grayscale[700]
    : whooshPrimitives.grayscale[0];

  const styles = StyleSheet.create({
    pressable: {
      outlineColor: "transparent",
      outlineWidth: 0,
    },
    trackContainer: {
      width: 32,
      height: 20,
      position: "relative",
    },
    focusOutline: {
      width: 36,
      height: 24,
      borderRadius: 100,
      borderColor: "#4688EC",
      borderWidth: 2,
      position: "absolute",
      top: -2,
      left: -2,
    },
    track: {
      width: 32,
      height: 20,
      borderRadius: 100,
      overflow: "hidden",
      position: "relative",
      backgroundColor: switchTrackColor,
    },
    thumb: {
      width: 14,
      height: 14,
      borderRadius: 24,
      position: "absolute",
      top: 3,
      backgroundColor: switchThumbColor,
    },
  });

  const webPressableProps =
    Platform.OS === "web"
      ? {
          "aria-checked": checkedState,
          onKeyDown,
          style: styles.pressable,
        }
      : {};

  return (
    <Pressable
      onPress={toggleSwitch}
      onHoverIn={onHover}
      onHoverOut={onHoverOut}
      onFocus={onFocus}
      onBlur={onBlur}
      accessibilityLabel={accessibilityLabel ?? switchLabel}
      accessibilityRole="switch"
      accessibilityState={{
        checked: checkedState,
        disabled: isDisabled,
      }}
      disabled={isDisabled}
      nativeID={name}
      testID={testID}
      {...webPressableProps}
    >
      <HStack gap={3} alignItems="center">
        {labelPosition === "left" ? (
          <ToggleSwitchLabel
            label={switchLabel}
            isDisabled={isDisabled}
            showToggleLabel={showToggleLabel}
          />
        ) : null}
        <Box style={styles.trackContainer}>
          {isSwitchFocused ? <Box style={styles.focusOutline} /> : null}
          <Box style={styles.track}>
            <Animated.View
              style={[
                styles.thumb,
                {
                  left: thumbLeft,
                },
              ]}
            />
          </Box>
        </Box>
        {labelPosition === "right" ? (
          <ToggleSwitchLabel
            label={switchLabel}
            isDisabled={isDisabled}
            showToggleLabel={showToggleLabel}
          />
        ) : null}
      </HStack>
    </Pressable>
  );
};
